import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { ErrorBox, Panel } from '../components';
import { t } from '../i18n';

interface UpdateStatus {
  enabled: boolean;
  boot_id: string;
  queued: boolean;
  status: null | {
    state: 'building' | 'restarting' | 'failed' | 'succeeded';
    message: string;
    revision?: string;
    updated_at: string;
  };
}

export function UpdateTab() {
  const [waiting, setWaiting] = useState<{ boot: string; since: number } | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const status = useQuery({
    queryKey: ['application-update'],
    queryFn: () => api.get<UpdateStatus>('/api/admin/update'),
    // An error (e.g. no updater on this server) stays until the page is reloaded. While an update runs, keep
    // polling through the errors of the restart to notice the new server.
    refetchInterval: (query) => (query.state.error && !waiting ? false : 3000),
    retry: false,
  });
  const update = useMutation({
    mutationFn: () => api.post<{ boot_id: string }>('/api/admin/update'),
    onSuccess: (result) => {
      setTimedOut(false);
      setWaiting({ boot: result.boot_id, since: Date.now() });
      void status.refetch();
    },
  });
  useEffect(() => {
    if (!waiting) return;
    if (status.data?.boot_id !== undefined && status.data.boot_id !== waiting.boot) {
      window.location.reload();
      return;
    }
    if (status.data?.status?.state === 'failed' && !status.data.queued) {
      setWaiting(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setTimedOut(true);
      setWaiting(null);
    }, Math.max(0, waiting.since + 31 * 60_000 - Date.now()));
    return () => window.clearTimeout(timer);
  }, [waiting, status.data]);
  const busy = waiting !== null || status.data?.queued || ['building', 'restarting'].includes(status.data?.status?.state ?? '');
  return (
    <Panel title={t('Application updates')}>
      <p>{t('Fetch the latest main branch, build an isolated release, then restart this application. The page reloads when the new server is ready.')}</p>
      <p>{t('Back up the database before updating. Commit and push local changes before using this button.')}</p>
      <ErrorBox error={update.error || (!waiting && status.error)} />
      {timedOut && <div className="banner error">{t('The update did not finish in time. Check the server update logs before retrying.')}</div>}
      {status.data && !status.data.enabled && <div className="banner info">{t('The application updater must be installed and enabled on this server first.')}</div>}
      {status.data?.status && (
        <p role="status">
          {status.data.status.state === 'succeeded' ? t('Application update completed.') : t(status.data.status.message)}
          {status.data.status.revision && <> ({status.data.status.revision.slice(0, 12)})</>}
        </p>
      )}
      {waiting && <p role="status">{t('Update requested. Waiting for the updated server; a brief connection interruption is expected.')}</p>}
      <button className="primary" disabled={!status.data?.enabled || busy || update.isPending || !!status.error}
        onClick={() => {
          if (window.confirm(t('Build and restart the Böning application from origin/main?'))) update.mutate();
        }}>
        {busy || update.isPending ? t('Updating…') : t('Update from Git and restart')}
      </button>
    </Panel>
  );
}
